import { useEditProductContext } from "../../../contexts/edit-product-context/useEditProductContext";
import { useState, useEffect, useRef } from "react";
import { getProductExtensionCategories } from "../../../service";
import { useQuery } from "react-query";
import { Section } from "../../pages/EditProduct";
import { Flex, Select, Switch } from "antd";
import { ExtensionCategory } from "./components/ExtensionCategory";
import { useAdditionsTour } from "../../../contexts/additions-tour-context/useAdditionsTour";

export const ExtensionConfigurator: React.FC = () => {
  const {
    setUnsavedChanges,
    productBaseVariants,
    setProductBaseVariants,
    extensionsConfig,
    setValidateExtensions,
  } = useEditProductContext();

  const [extensionsCategory, setExtensions] = useState([]);
  const [invalidCategories, setInvalidCategories] = useState<number[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const { isTourOpen, activeTour, tourStep, advanceStep, firstExtensionCategoryRef } = useAdditionsTour();

  const { isLoading } = useQuery<any, Error>(
    ["/product-extension-categories"],
    () => getProductExtensionCategories(),
    {
      onSuccess(data) {
        setExtensions(
          data.sort((a: any, b: any) => (a.menuPosition ?? 0) - (b.menuPosition ?? 0))
        );
      },
    }
  );

  // Funkcja walidacji
  useEffect(() => {
    const validate = () => {
      const invalid: number[] = [];

      extensionsCategory.forEach((category: any) => {
        const options = category.extensions
          ?.map((e: any) => e?.productExtensionVariant)
          .flat()
          .filter(
            (obj: any, index: any, self: any) =>
              index === self.findIndex((o: any) => o.name === obj.name)
          );

        // Sprawdź tylko gdy grupa ma więcej niż 1 wariant cenowy
        if (options && options.length > 1) {
          // Sprawdź czy dla każdego wariantu produktu został wybrany wariant cenowy
          productBaseVariants.forEach((productVariant) => {
            const hasSelectedExtensions = category.extensions?.some((ext: any) => {
              return extensionsConfig?.[productVariant.key]?.[ext.id];
            });

            if (hasSelectedExtensions) {
              // Sprawdź czy wszystkie wybrane dodatki mają przypisany wariant cenowy
              const allExtensionsHaveVariant = category.extensions?.every((ext: any) => {
                const config = extensionsConfig?.[productVariant.key]?.[ext.id];
                if (!config) return true; // Nie jest wybrany, więc OK
                return config.name && config.name !== undefined; // Jest wybrany, musi mieć name
              });

              if (!allExtensionsHaveVariant) {
                if (!invalid.includes(category.id)) {
                  invalid.push(category.id);
                }
              }
            }
          });
        }
      });

      setInvalidCategories(invalid);

      // Rozwiń kategorie z błędami
      if (invalid.length > 0) {
        setExpandedCategories(prev => {
          const combined = [...prev, ...invalid];
          return Array.from(new Set(combined));
        });

        // Scroll do pierwszej nieprawidłowej kategorii
        setTimeout(() => {
          const firstInvalidId = invalid[0];
          const element = categoryRefs.current[firstInvalidId];
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }

      return { isValid: invalid.length === 0, invalidCategories: invalid };
    };

    setValidateExtensions(validate);
  }, [extensionsCategory, extensionsConfig, productBaseVariants, setValidateExtensions]);

  return (
    <>
      {extensionsCategory.map((category: any, idx: number) => {
        const options = category.extensions
          ?.map((e: any) => e?.productExtensionVariant)
          .flat()
          .filter(
            (obj: any, index: any, self: any) =>
              index === self.findIndex((o: any) => o.name === obj.name)
          )
          .sort((a: any, b: any) => a.id - b.id)
          ?.map((e: any) => {
            return {
              label: e.name,
              value: e.name,
            };
          });

        return (
          <div
            key={category.id}
            ref={(el) => {
              categoryRefs.current[category.id] = el;
              // Attach tour ref to first category only when attach-additions tour is active
              if (idx === 0 && isTourOpen && activeTour === "attach-additions") {
                (firstExtensionCategoryRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }
            }}
          >
            <ExtensionCategory
              options={options}
              category={category}
              hasError={invalidCategories.includes(category.id)}
              isExpanded={expandedCategories.includes(category.id)}
              isFirstForTour={idx === 0}
              onExpandChange={(expanded: boolean) => {
                setExpandedCategories(prev =>
                  expanded
                    ? [...prev, category.id]
                    : prev.filter(id => id !== category.id)
                );
                // Advance tour when user interacts with first extension category
                if (idx === 0 && isTourOpen && activeTour === "attach-additions" && (tourStep === 4 || tourStep === 5)) {
                  setTimeout(() => advanceStep(tourStep), 400);
                }
              }}
            />
          </div>
        );
      })}
    </>
  );
};
